import type { TaskType } from "../prop-types";
import type { Column } from "@bryntum/grid-thin";
import type { GanttTearsheetRef } from "../gantt-tearsheets";
import type { MutableRefObject, RefObject } from "react";
import type { BryntumSchedulerPro } from "@bryntum/schedulerpro-react-thin";
import type { ResourceModel } from "@bryntum/scheduler-thin";
import type { EventModel } from "@bryntum/schedulerpro-thin";
import type { ViewPreset } from "@bryntum/scheduler-thin";
import type { useI18nContext } from "@procore/core-react";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import type { ResourceType } from "@/react/prop-types";

export const SCHEDULER_EVENT_LAYOUT_KEY = "scheduler-pro-event-layout";

export const NonWorkingDayWarning = {
   Both: "both",
   Start: "start",
   End: "end",
} as const;

export type NonWorkingDayWarning = (typeof NonWorkingDayWarning)[keyof typeof NonWorkingDayWarning];

export const loadUrl = Assignment2Store.getFullRequestUrl({ path: "/api/v3/resources/load" });
export const syncUrl = Assignment2Store.getFullRequestUrl({ path: "/api/v3/resources/sync" });

export type EventTooltipProps = {
   type: TaskType;
   projectName: string;
   projectJobNumber: string;
   dateRangeString: string;
   startTime: string;
   endTime: string;
   isTaskWithTime: boolean;
   isEquipment: boolean;
   taskTimeAllocation: string;
   workingDayClass: (day: number) => string;
   jobTitleName: string | null;
   jobTitleColor: string | null;
   paidShiftHours: number;
   taskName: string;
};

export type SchedulerResource = {
   id: string;
   resourceId: string;
   resourceType: ResourceType;
   name: string;
   jobTitle: string;
   profilePicUrl: string;
   initials: string;
   jobTitleColor: string | null;
   statusColor: string | null;
   statusAbbreviation: string | null;
   statusName: string | null;
};

export type SchedulerEvent = {
   id: string;
   resourceType: ResourceType;
   resourceName: string;
   projectName: string;
   startDate: Date;
   endDate: Date;
   eventColor: string;
   projectColor: string | null;
   projectJobNumber: string;
   assignmentId: string;
   projectId: string;
   percentAllocated: number | null;
   startTime: number | string | null;
   endTime: number | string | null;
   workDays: Record<string, boolean> | null;
   jobTitleName: string | null;
   jobTitleColor: string | null;
   overtime: boolean;
   statusId?: string | null;
   statusName?: string | null;
   statusColor?: string | null;
   statusAbbreviation?: string | null;
};

/**
 * Shape of the "newTask" object passed from the edit-assignment tearsheet callback.
 * Used when mapping back to SchedulerEvent for in-place resource schedule updates.
 */
export type NewTaskFromTearsheet = Partial<{
   projectId: string;
   projectName: string;
   name: string;
   projectJobNumber: string;
   jobNumber: string;
   projectColor: string | null;
   startDate: Date;
   endDate: Date;
   startTime: string | null;
   endTime: string | null;
   percentAllocated: number | null;
   workDays: Record<string, boolean> | null;
   jobTitleName: string | null;
   jobTitleColor: string | null;
   overtime: boolean;
   eventColor: string | null;
   status: {
      id?: string;
      name?: string;
      color?: string;
      abbreviation?: string;
   };
   statusId: string | null;
   statusName: string | null;
   statusColor: string | null;
   statusAbbreviation: string | null;
}>;

export type DragCreateContext = {
   finalize: (v: boolean) => void;
};

export type DragCreateAssignmentParams = {
   dragCreateContext?: DragCreateContext;
   newTask: NewTaskFromTearsheet & { id: string; resourceId?: string };
   schedulerRef: RefObject<BryntumSchedulerPro>;
};

export type DragCreateState = {
   context: DragCreateContext;
   resourceRecord: ResourceModel;
   phantomEventRecord: EventModel;
   finalized?: boolean;
};

export type BeforeDragCreateFinalizeParams = {
   dragCreateContextRef: MutableRefObject<DragCreateState | null>;
   createAssignmentTearsheetRef: RefObject<GanttTearsheetRef> | undefined;
   schedulerRef: RefObject<BryntumSchedulerPro>;
   handleDragCreate: (params: DragCreateAssignmentParams) => Promise<void>;
};

export type SchedulerAssignment = {
   id: string;
   eventId: string;
   resourceId: string;
};

export type SchedulerData = {
   resources: SchedulerResource[];
   events: SchedulerEvent[];
   assignments: SchedulerAssignment[];
};

export type SchedulerInitialDateRange = {
   minDate: Date;
   maxDate: Date;
   viewPreset: string;
   visibleDate: Date;
};

export type ResourceScheduleProps = {
   paidShiftHours: number;
   personDetailsTearsheetRef: RefObject<GanttTearsheetRef>;
   editAssignmentTearsheetRef?: RefObject<GanttTearsheetRef>;
   createAssignmentTearsheetRef?: RefObject<GanttTearsheetRef>;
   canViewPeople: boolean;
   loadParams?: Record<string, any>;
   availablePresets: ViewPreset[];
   viewPreset: ViewPreset;
   schedulerRef: RefObject<BryntumSchedulerPro>;
   hasPermissionToViewPeopleTimeoff: boolean;
   initialDateRange?: SchedulerInitialDateRange;
};

export enum EventLayout {
   STACK = "stack",
   NONE = "none",
}

export type HeaderRenderData = {
   column: Column & { grid: { eventLayout: string } };
   headerElement: HTMLElement;
};

export type TimeOffTooltipData = {
   name: string;
   jobTitleName: string;
   jobTitleColor: string;
   timeOffReason: string;
   startDate: Date;
   endDate: Date;
   startTime: string;
   endTime: string;
   isPaid: boolean;
   applyToSaturday: boolean;
   applyToSunday: boolean;
   repeat: string;
};

export type TimeOffTooltipProps = TimeOffTooltipData & {
   I18n: ReturnType<typeof useI18nContext>;
};

export enum SchedulerElementClass {
   EVENT = "b-sch-event",
   RESOURCE_NONWORKING = "b-sch-resource-non-working-time",
   EVENT_STRIPE = "b-sch-non-working-time",
}

export enum TooltipElementType {
   EVENT = "event",
   RESOURCE_NONWORKING = "resource-nonworking",
   EVENT_STRIPE = "event-stripe",
   UNKNOWN = "unknown",
}

export type DragResizeRecord = Pick<
   SchedulerEvent,
   "startTime" | "endTime" | "percentAllocated" | "workDays"
>;

export const DragResizeRecordKey = {
   EVENT_RECORD: "eventRecord",
   RECORD: "record",
} as const;

export type DragResizeRecordKey = (typeof DragResizeRecordKey)[keyof typeof DragResizeRecordKey];

export type EventRecordForUnassign = { getData?: (key: string) => unknown; [key: string]: unknown };
